// V2 priced roller shades use Shopify's cart; legacy drafts stay separate.
(function(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.NoDrillCartBridge = api;
    const boot = () => document.querySelectorAll('[data-no-drill-configurator]').forEach(api.setup);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
    else boot();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
  'use strict';
  const remoteLabels = { one_channel: '1-channel', six_channel: '6-channel', sixteen_channel: '16-channel' };
  const channelLabels = { none: 'None', l_channel: 'L channel', u_channel: 'U channel' };
  // Cart icon number: shades plus remotes and other stand-alone add-ons; side channels ride with their shade.
  const badgeCount = cart => (cart?.items || []).reduce((sum, item) => sum + (item?.properties?._ndr_line_role === 'side_channel' ? 0 : Number(item?.quantity) || 0), 0);
  const remoteLabel = configuration => configuration.remoteType === 'none' ? 'Using another remote'
    : `${remoteLabels[configuration.remoteType]} remote (x${configuration.remoteQuantity})`;

  // Checkout and the order email show only these short lines (Albert, Oct 13). The full record stays on the
  // order under hidden "_" names, which Shopify keeps for the admin, the order sheet and the cart page.
  function hiddenRecord(record) {
    return Object.fromEntries(Object.entries(record).map(([k,v]) => [k.startsWith('_') ? k : `_${k}`, v]));
  }
  function shopperLines(r) {
    const lower = v => String(v || '').toLowerCase();
    const side = r['Control side'];
    const control = !r.Control ? '' : !side ? r.Control
      : r.Control === 'Motorized' ? `Motorized, charging port on the ${lower(side)}` : `${r.Control}, ${lower(side)} side`;
    const lines = [
      ['Color', r.Color],
      ['Size', r['Entered Width'] && r.Height ? `${r['Entered Width']} wide × ${r.Height} tall` : ''],
      ['Control', control],
      ['Headrail', [r['Headrail style'], lower(r['Hardware finish'])].filter(Boolean).join(', ')],
      ['Bottom rail', [r['Bottom rail'], lower(r['Bottom rail finish'])].filter(Boolean).join(', ')],
      ['Room', fullName(r.Room, r['Room / Window Label'])],
      ['Remote', r.Remote === 'Using another remote' ? r.Remote : ''],
    ];
    return Object.fromEntries(lines.filter(([,v]) => v).map(([k,v]) => [k, String(v)]));
  }

  // Albert (Oct 17): the room is required and the window name is optional. A blank name becomes
  // "Room, Window N", numbered within that room from the shade lines already in the cart
  // ("Bedroom 2, Window 1"). A typed name is kept. Editing a shade keeps its automatic name in the same room.
  const autoLabel = (room, n) => room ? `${room}, Window ${n}` : `Window ${n}`;
  const autoPattern = room => new RegExp(`^${autoLabel(room, '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\d+)$`, 'i');
  const lineLabel = p => String(p['_Room / Window Label'] || p['Room / Window Label'] || '').trim();
  // Since Oct 13 the raw room is the hidden "_Room"; the visible "Room" line joins room and name.
  const lineRoom = p => String(('_Room / Window Label' in p ? p._Room : p.Room) || '').trim();
  // "Kitchen" + "Kitchen, Window 1" reads "Kitchen, Window 1", not "Kitchen - Kitchen, Window 1".
  function fullName(room, label) {
    const r = String(room || '').trim(), l = String(label || '').trim();
    const lower = l.toLowerCase(), key = r.toLowerCase();
    if (!r || !l) return r || l;
    return lower === key || lower.startsWith(`${key},`) || lower.startsWith(`${key} `) ? l : `${r} - ${l}`;
  }
  function nextWindowName(cart, room, editingId) {
    const target = String(room || '').trim();
    const shades = (cart?.items || []).filter(item => item?.properties?._ndr_line_role === 'shade');
    const own = editingId && shades.find(item => item.properties._configuration_id === editingId);
    if (own && lineRoom(own.properties) === target && autoPattern(target).test(lineLabel(own.properties))) return lineLabel(own.properties);
    let count = 0, highest = 0;
    for (const item of shades) {
      if ((editingId && item.properties._configuration_id === editingId) || lineRoom(item.properties) !== target) continue;
      count += Math.max(1, Number(item.quantity) || 1);
      const match = autoPattern(target).exec(lineLabel(item.properties));
      if (match) highest = Math.max(highest, Number(match[1]));
    }
    return autoLabel(target, Math.max(highest, count) + 1);
  }
  function windowName(typed, cart, editingId, room) {
    return String(typed || '').trim() || nextWindowName(cart, room, editingId);
  }

  function properties(configuration, rules, category, core, configurationId, sku) {
    const optionSummary = rules.optionGroups
      .filter(group => !group.hideFromSummary && category.isGroupActive(group, configuration.options))
      .map(group => ({id: group.id, label: group.label,
        value: group.options.find(option => option.id === configuration.options[group.id])?.label || ''}));
    if (configuration.control === 'pull_chain') {
      optionSummary.push({id:'chain_material', label:'Chain material',
        value:rules.controls.pull_chain.childOptions[configuration.options.chain_material]?.label || ''});
    }
    const NOT_ASKED = 'Not asked (outside mount)';
    const wallAsked = configuration.mountType !== 'outside' || ['flat', 'textured'].includes(configuration.mountingSurface);
    const result = core.buildLineItemProperties({
      ...configuration, optionSummary, configurationId, priceBandId:sku,
      categoryLabel:rules.displayLabel,
      mountSystemLabel:rules.mounts[configuration.mountSystem]?.label,
      mountLocationLabel:configuration.mountType === 'outside' ? 'Outside mount' : 'Inside mount',
      controlLabel:rules.controls[configuration.control]?.label,
      accessoryLabels:[],
      // The stepped builder skips the wall question for outside mounts; never invent an answer for the factory.
      mountingSurfaceLabel:wallAsked ? (configuration.mountingSurface === 'flat' ? 'Flat / smooth' : 'Textured / uneven') : NOT_ASKED,
      openingMaterialLabel:wallAsked && configuration.openingMaterial ? (configuration.openingMaterialLabel || configuration.openingMaterial) : NOT_ASKED,
    });
    // Customer input is not a factory-cut dimension. Deductions remain a launch gate, so the
    // entered width replaces the core's "Production Width" label in the same position.
    const entries = Object.entries(result).map(([k,v]) => k === 'Production Width'
      ? ['Entered Width', core.formatInches(configuration.width)] : [k,v]);
    entries.push(['Side channels', channelLabels[configuration.sideChannel || 'none']]);
    if (configuration.control === 'motorized') entries.push(['Remote', remoteLabel(configuration)]);
    entries.push(['_ndr_line_role','shade'], ['_ndr_cart_version','2']);
    const record = Object.fromEntries(entries.filter(([,v]) => v != null && String(v).length).map(([k,v])=>[k,String(v)]));
    return {...shopperLines(record), ...hiddenRecord(record)};
  }
  function items(configuration, rules, category, core, configurationId, resolved) {
    const label = fullName(configuration.room, configuration.roomLabel);
    return {
      items: resolved.items.map(line => {
        const shared = { _configuration_id: configurationId, _ndr_line_role: line.role, _ndr_cart_version: '2' };
        const details = line.role === 'shade' ? properties(configuration,rules,category,core,configurationId,line.sku)
          : line.role === 'side_channel' ? { ...shared, Room: configuration.room,
            'Room / Window Label': configuration.roomLabel, For: `${label} shade`, 'Channel type': channelLabels[configuration.sideChannel] }
          : { ...shared, Remote: remoteLabel(configuration), 'Added with': label };
        return { id: line.id, quantity: line.quantity, properties: details };
      }),
      expectedUnitCents: resolved.items.map(line => line.unitCents),
    };
  }
  function quote(configuration, rules, core, pricing, client) {
    const validation = core.validateConfiguration(configuration, rules);
    // The step builder needs a room for every window (the name stays optional).
    if (rules?.steppedBuilder && !String(configuration.room || '').trim()) {
      return {valid:false, reason:'room-required', errors:['Pick a room.', ...(validation.errors || [])]};
    }
    if (!validation.valid) return validation;
    const price = pricing.priceConfiguration(configuration);
    const resolved = client.resolveItems(configuration, price);
    const unavailable = ['addons-unavailable', 'addon-price-mismatch'].includes(resolved.reason);
    return resolved.valid ? {...resolved, errors:[]} : {...resolved, errors:[unavailable
      ? 'You cannot add extras right now. Please try again later.' : 'This selection is not available to add to cart yet.']};
  }
  function saveEditRecord(id, configuration, replacedId) {
    // This device-only shortcut must never prevent an otherwise successful cart add.
    try {
      const stored = JSON.parse(globalThis.localStorage.getItem('ndr-cart-edit-v1') || '{}');
      const records = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
      if (replacedId) delete records[replacedId];
      const url = new URL(globalThis.location.pathname + globalThis.location.search, globalThis.location.origin);
      url.searchParams.delete('edit_line');
      records[id] = {v:1, savedAt:Date.now(), editUrl:url.pathname + url.search, configuration};
      const newest = Object.entries(records).sort((a,b) => (b[1]?.savedAt || 0) - (a[1]?.savedAt || 0)).slice(0,50);
      globalThis.localStorage.setItem('ndr-cart-edit-v1', JSON.stringify(Object.fromEntries(newest)));
    } catch (error) { /* Storage may be blocked or full. The cart remains authoritative. */ }
  }
  async function capturePicture() {
    let timer;
    try {
      if (typeof globalThis.NDRWindowPreview?.capture !== 'function') return {blob:null};
      // Invoke now, before any cart read: the renderer must snapshot the clicked setup.
      const rendered = globalThis.NDRWindowPreview.capture();
      const picture = await Promise.race([rendered, new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('Picture timed out')), 6000);
      })]);
      if (picture === null) return {blob:null};
      if (!(picture?.blob instanceof Blob) || !['image/jpeg', 'image/png'].includes(picture.blob.type)) throw new Error('Picture unavailable');
      return {blob:picture.blob};
    } catch (_) { return {blob:null, failed:true}; }
    finally { clearTimeout(timer); }
  }
  // Device storage must never turn a confirmed purchase into a retry or duplicate add.
  async function localPicture(action, ...args) {
    try { return await globalThis.NoDrillPhotoStore?.[action]?.(...args) || {ok:false}; }
    catch (_) { return {ok:false}; }
  }
  function setup(section) {
    const core = globalThis.NoDrillConfiguratorCore;
    const category = globalThis.NoDrillCategoryConfig;
    const pricing = globalThis.NoDrillTieredPricePreview;
    const client = globalThis.NoDrillCartClient;
    const bootstrap = section.querySelector('[data-configurator-bootstrap]');
    if (!core || !category || !pricing || !client || !bootstrap || globalThis.location.pathname.endsWith('.html')) return;
    const data = JSON.parse(bootstrap.textContent);
    // Page locks take precedence over URL values, as in the configurator.
    const params = new URLSearchParams(globalThis.location.search);
    ['fabric_family', 'lock_fabric_family', 'fabric_type', 'lock_fabric_type'].forEach((key) => {
      const value = data.pageLocks?.[key];
      if (typeof value === 'string' && value) params.set(key, value);
    });
    const baseRules = category.buildRules(data.categoryKey);
    const rules = category.buildRules(data.categoryKey, category.resolveFamilyPolicyContext?.(baseRules, [
      ...params.getAll('lock_fabric_family'), ...params.getAll('fabric_family')]) || {});
    if (rules.mode !== 'catalog-v2' || rules.id !== 'roller-shades' || section.dataset.cartBridge) return;
    section.dataset.cartBridge = 'shopify';
    const form = section.querySelector('[data-configurator-form]');
    const add = section.querySelector('[data-add-another-window]');
    const view = section.querySelector('[data-review-order]');
    const note = section.querySelector('[data-order-note]');
    const status = section.querySelector('[data-configurator-status]');
    const error = section.querySelector('[data-error-summary]');
    // Success pop-up (Albert, Oct 16): View cart or Add another window. Missing on other templates.
    const found = section.querySelector('[data-cart-success-dialog]');
    const success = typeof found?.querySelector === 'function' ? found : null;
    const cart = client.create({fetch:globalThis.fetch.bind(globalThis),root:globalThis.Shopify?.routes?.root || '/'});
    function cartURL() {
      const url = new URL(`${globalThis.Shopify?.routes?.root || '/'}cart`,globalThis.location.origin);
      const preview = new URLSearchParams(globalThis.location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id',preview);
      return url.href;
    }
    function closeSuccess() {
      if (!success?.open) return;
      if (typeof success.close === 'function') success.close(); else success.removeAttribute('open');
    }
    function openSuccess(name) {
      if (!success) return;
      const label = success.querySelector('[data-cart-success-name]');
      if (label) label.textContent = name;
      const link = success.querySelector('[data-cart-success-view]');
      if (link) link.href = cartURL();
      if (success.open) return;
      if (typeof success.showModal === 'function') success.showModal(); else success.setAttribute('open','');
    }
    if (success) {
      success.querySelector('[data-cart-success-another]')?.addEventListener('click', () => {
        closeSuccess();
        // The last window is confirmed in the cart. A new window, even one just like it, gets a new
        // ID; reusing the old attempt would match the lines already in the cart and add nothing.
        message=''; submitted=''; attempt=null;
        // no-drill-builder-steps.js keeps the fabric and choices and clears size, room and name.
        section.dispatchEvent(new CustomEvent('ndr:add-another-window'));
      });
      // A tap on the dark area around the box closes it, like the Escape key.
      success.addEventListener('click', event => { if (event.target === success) closeSuccess(); });
    }
    let busy = false;
    let submitted = '';
    let savedEdit = '';
    let attempt = null;
    let message = '';
    function state() {
      const savedConfiguration = JSON.parse(section.dataset.configuration || '{}');
      const configuration = {...savedConfiguration};
      configuration.openingMaterialLabel = form.elements.openingMaterial?.selectedOptions?.[0]?.textContent?.trim() || '';
      const currentRules = rules;
      const fingerprint = JSON.stringify(configuration);
      const resolved = quote(configuration, currentRules, core, pricing, client);
      return {configuration, savedConfiguration, currentRules, fingerprint, resolved};
    }
    function refresh() {
      const s = state();
      add.disabled = busy || !s.resolved.valid || submitted === s.fingerprint;
      const editing = Boolean(section.dataset.editLine);
      const added = submitted === s.fingerprint;
      add.textContent = busy ? (editing ? 'Saving…' : 'Adding…') : added
        ? (savedEdit === s.fingerprint ? 'Changes saved' : 'Added to cart') : editing ? 'Save changes' : 'Add to cart';
      add.className = added ? 'button button--secondary' : 'button';
      view.className = added ? 'button' : 'button button--secondary';
      view.disabled = busy;
      view.textContent = 'View cart';
      note.textContent = 'Each window is added on its own. It keeps its size, fabric and choices.';
      status.textContent = message || (editing ? 'You are changing a shade in your cart. Save to update it.'
        : section.dataset.editLineMissing === 'true' ? 'We could not open that shade. Set it up again, or remove it in your cart.'
        : s.resolved.valid ? 'Ready to add to cart.'
        : ['addons-unavailable', 'addon-price-mismatch'].includes(s.resolved.reason) ? s.resolved.errors[0]
        : section.dataset.builderSteps === 'true' && s.resolved.errors?.[0] ? s.resolved.errors[0]
        : 'Make the choices we need. Confirm the details we ask for. Then add this window.');
    }
    form.addEventListener('input', () => {message='';});
    form.addEventListener('change', () => {message='';});
    // The configurator rewrites its buttons on every render and then stores the configuration here.
    new MutationObserver(refresh).observe(section,{attributes:true,attributeFilter:['data-configuration']});
    view.addEventListener('click', event => {
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy) return;
      globalThis.location.href = cartURL();
    },true);
    add.addEventListener('click', async event => {
      event.preventDefault(); event.stopImmediatePropagation();
      if (busy) return;
      const s = state();
      if (!s.resolved.valid || submitted === s.fingerprint) return;
      if (!attempt || attempt.fingerprint !== s.fingerprint) {
        attempt = {fingerprint:s.fingerprint,id:`ndr-${globalThis.crypto.randomUUID()}`};
      }
      const thisAttempt = attempt;
      const oldId = section.dataset.editLine;
      busy=true;message='';refresh();
      try {
        // Keep the first clicked snapshot for retries of this ID, even if the room photo changes.
        if (!thisAttempt.picture) thisAttempt.picture = await capturePicture();
        // Reconcile a lost response before another click can send the same window twice.
        const before = await cart.get();
        updateRemoteState(before);
        // One name per attempt, so a retry sends the same lines as the first try.
        if (!thisAttempt.windowName) thisAttempt.windowName = windowName(s.configuration.roomLabel, before, oldId, s.configuration.room);
        const named = {...s.configuration, roomLabel:thisAttempt.windowName};
        const request = items(named,s.currentRules,category,core,thisAttempt.id,s.resolved);
        if (!cart.reconcile(request.items,request.expectedUnitCents,before)) {
          await cart.addItems(request.items,request.expectedUnitCents);
        }
        submitted=s.fingerprint;
        let pictureWarning = thisAttempt.picture.failed;
        let pictureLimit = false;
        if (thisAttempt.picture.blob) {
          const saved = await localPicture('save', thisAttempt.id, thisAttempt.picture.blob);
          pictureWarning = !saved.ok;
          pictureLimit = saved.reason === 'limit';
        }
        // Once confirmed, release the in-memory picture. It is never sent with line properties.
        thisAttempt.picture = {blob:null};
        saveEditRecord(thisAttempt.id, {...s.savedConfiguration, roomLabel:thisAttempt.windowName});
        let removalFailed = false;
        if (oldId) {
          savedEdit=s.fingerprint;
          try {
            // Keys can change after each mutation. Never remove a line from another setup.
            let current = await cart.get();
            let oldLine;
            while ((oldLine = current.items.find(item => item.properties?._configuration_id === oldId))) {
              await cart.remove(oldLine.key);
              current = await cart.get();
            }
          } catch (failure) { removalFailed = true; }
        }
        let cleanupFailed = false;
        if (oldId && !removalFailed) {
          saveEditRecord(thisAttempt.id, {...s.savedConfiguration, roomLabel:thisAttempt.windowName}, oldId);
          cleanupFailed = !(await localPicture('remove', oldId)).ok;
        }
        if (oldId) {
          const url = new URL(globalThis.location.pathname + globalThis.location.search, globalThis.location.origin);
          url.searchParams.delete('edit_line');
          globalThis.history.replaceState(null, '', url.pathname + url.search);
          delete section.dataset.editLine;
        }
        delete section.dataset.editLineMissing;
        message = oldId ? (removalFailed
          ? 'Your new shade is in your cart. Please remove the old one in your cart.'
          : 'Saved. Your cart has the new details.')
          : 'Window added to your cart.';
        if (pictureWarning) message += pictureLimit
          ? ' Your shade was added, but its picture was not saved. This browser has hit the picture limit (50 pictures or 20 MB).'
          : ' Your shade was added, but its picture was not saved on this device.';
        if (cleanupFailed) message += ' We could not clear the old picture from this browser.';
        if (removalFailed || pictureWarning || cleanupFailed) { error.textContent=message;error.hidden=false;error.focus(); }
        // The pop-up shows only once the cart has confirmed a new window, never for a saved edit.
        if (!oldId) openSuccess(fullName(s.configuration.room, thisAttempt.windowName));
        // A header refresh must not replace a confirmed edit outcome with a generic error.
        const count = await cart.get().catch(() => null);
        if (!count) return;
        updateRemoteState(count);
        document.querySelectorAll('.ndr-chrome__cart').forEach(link=>{
          const shown=badgeCount(count);
          const number=link.querySelector('strong');if(number){number.textContent=String(shown);number.hidden=!shown;}
          link.setAttribute('aria-label',`Cart with ${shown} items`);
        });
      } catch (failure) {
        message=`${failure.message || 'Could not confirm the cart change.'} View your cart before you try again.`;
        error.textContent=message;error.hidden=false;error.focus();
      } finally {busy=false;refresh();}
    },true);
    function updateRemoteState(current) {
      section.dataset.cartHasRemote = String(current.items.some(item => item.properties?._ndr_line_role === 'remote'));
      if (typeof globalThis.CustomEvent === 'function') section.dispatchEvent(new CustomEvent('ndr:cart-remote-status', {
        detail: { hasRemote: section.dataset.cartHasRemote === 'true' },
      }));
      // The stepped builder shows the next automatic window name as the name field hint.
      if (typeof globalThis.CustomEvent === 'function') section.dispatchEvent(new CustomEvent('ndr:cart-window-names', {detail: current}));
    }
    // This read only informs the existing-remote note; a failed read is unknown, not an empty cart.
    cart.get().then(updateRemoteState).catch(() => { section.dataset.cartHasRemote = 'unknown'; });
    refresh();
  }
  return {properties,items,quote,setup,windowName,nextWindowName,badgeCount};
});
